import { gql, MutationResult, useMutation } from '@apollo/client';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback } from 'react';
import { MAYA_GRAPHQL_ENDPOINT } from 'basics/constants/common.constants';
import { MutationReturnType } from 'basics/types/maya.types';
import { extractApolloErrorDetails } from 'services/graphql/extractApolloErrorDetails';
import Logger from 'services/logger';

export const setCepStatusToCancelledMutationCall = gql`
  mutation setCepStatusToCancelled($id: String!, $sendToVeeva: Boolean!) {
      setCepStatusToCancelled(id: $id, sendToVeeva: $sendToVeeva)
  }
`;

type ApiResultType = {
  setCepStatusToCancelled: boolean;
};

type UseSetCepStatusToCancelledMutation = [
  (
    id: string,
    sendToVeeva?: boolean
  ) => Promise<MutationReturnType<boolean>>,
  MutationResult<ApiResultType>,
];

export const useSetCepStatusToCancelledMutation = (): UseSetCepStatusToCancelledMutation => {
  const [setCepStatusToCancelled, state] = useMutation<ApiResultType>(setCepStatusToCancelledMutationCall, {
    fetchPolicy: 'network-only',
    context: { clientName: MAYA_GRAPHQL_ENDPOINT },
  });
  const flags = useFlags();

  const executeMutation = useCallback(async (id, sendToVeeva = true) => {
    try {
      const { data } = await setCepStatusToCancelled({
        variables: {
          id,
          sendToVeeva: flags.mayaEnableVeevaBiogenlinc1816 && sendToVeeva,
        },
      });

      return { data: !!data?.setCepStatusToCancelled, error: null };
    } catch (error: unknown) {
      if (error instanceof Error) {
        const customContext = extractApolloErrorDetails(error);
        Logger.logError({
          error,
          customContext,
          transactionName: 'setCepStatusToCancelled.ts - useSetCepStatusToCancelledMutation',
        });

        return { data: false, error: customContext.message as string };
      }

      return { data: false, error: null };
    }
  }, [setCepStatusToCancelled, flags.mayaEnableVeevaBiogenlinc1816]);

  return [executeMutation, state];
};
